import Button from '@cobliteam/colib-ui/Button';
import Checkbox from '@cobliteam/colib-ui/Checkbox';
import { cn as _cnSelection, cn } from '@cobliteam/colib-ui/cn';
import Empty, { EmptyDescription, EmptyHeader, EmptyMedia } from '@cobliteam/colib-ui/Empty';
import Icon from '@cobliteam/colib-ui/Icon';
import InputGroup, { InputGroupAddon, InputGroupInput } from '@cobliteam/colib-ui/InputGroup';
import Label from '@cobliteam/colib-ui/Label';
import Typography from '@cobliteam/colib-ui/Typography';
import { Location, type VehicleGroup } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { usePermissions } from '@cobliteam/permissions';
import SelectionList, { SelectionListLoading } from '@cobliteam/ui-business/components/SelectionList/SelectionList';
import TooltipOnIntent from '@cobliteam/ui-business/components/TooltipOnIntent/TooltipOnIntent';
import type React from 'react';
import { useState } from 'react';

export interface MultiSelectVehicleGroupsSidebarOverlayProps {
  isLoading?: boolean;
  vehicleGroups: VehicleGroup[];
  selectedGroups: VehicleGroup[];
  selectionChanged: boolean;
  disableGroupsWithoutVehicle?: boolean;
  limit?: number;
  onChange: (group: VehicleGroup) => void;
  onClearFilter: () => void;
  onApplyFilter: () => void;
  onCancel: () => void;
}

const MultiSelectVehicleGroupsSidebarOverlay: React.FC<MultiSelectVehicleGroupsSidebarOverlayProps> = ({
  isLoading,
  selectedGroups,
  vehicleGroups,
  selectionChanged,
  disableGroupsWithoutVehicle = true,
  limit,
  onChange,
  onClearFilter,
  onApplyFilter,
  onCancel,
}) => {
  const { t } = useTranslation('multiselects');
  const [searchBoxInput, setSearchBoxInput] = useState('');
  const [selectedGroupsDraft] = useState<VehicleGroup[]>(selectedGroups);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [_sortOrderLabel, setSortOrderLabel] = useState('A-Z');

  const { isAccessAllowed } = usePermissions();
  const hasUpdateVehiclePermission = isAccessAllowed('create-update-delete:vehicle');

  const handleCreateGroup = () => {
    window.open(`${window.location.origin}${Location.VEHICLES_GROUPS}/new`, '_blank')!.focus();
  };

  const renderNoGroupsPlaceholder = () => (
    <div>
      <div className='m-7 flex flex-col flex-wrap items-center justify-center gap-3'>
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <Icon name='cb-placeholder-vehicle-group' size='md' />
            </EmptyMedia>
            <EmptyDescription>{vehicleGroups.length === 0 ? t('groups.no_groups_registered') : t('groups.no_group_with_name')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
        {hasUpdateVehiclePermission && (
          <Button size='sm' onClick={handleCreateGroup}>
            {t('groups.create_groups')}
          </Button>
        )}
      </div>
    </div>
  );

  const swapSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    setSortOrderLabel(sortOrder === 'asc' ? t('groups.sort_za') : t('groups.sort_az'));
  };

  const sortByName = (a: VehicleGroup, b: VehicleGroup) => {
    if (sortOrder === 'asc') return a.name.toLowerCase().localeCompare(b.name.toLowerCase());

    return b.name.toLowerCase().localeCompare(a.name.toLowerCase());
  };

  const sortBySelected = (a: VehicleGroup, b: VehicleGroup) => {
    if (selectedGroupsDraft.find((g) => g.id === a.id) && !selectedGroupsDraft.find((g) => g.id === b.id)) return -1;
    if (!selectedGroupsDraft.find((g) => g.id === a.id) && selectedGroupsDraft.find((g) => g.id === b.id)) return 1;
    return 0;
  };

  const filterOptionsBySearchBoxInput = () => {
    const normalizedSearchBoxInput = searchBoxInput.trim().toLowerCase();
    if (normalizedSearchBoxInput.length > 0) {
      return vehicleGroups.filter((group) => group.name.toLocaleLowerCase().includes(normalizedSearchBoxInput));
    }
    return [...vehicleGroups];
  };

  const filteredOptions = filterOptionsBySearchBoxInput();

  const groupsFiltered: VehicleGroup[] = filteredOptions.sort(sortByName).sort(sortBySelected);

  const validateGroupHasNoVehicle = (group: VehicleGroup): boolean => (disableGroupsWithoutVehicle && group.vehicleIds && group.vehicleIds.length === 0) ?? false;

  const disabled = (group: VehicleGroup) =>
    validateGroupHasNoVehicle(group) || (selectedGroups.find((g) => g.id === group.id) === undefined && limit != null && selectedGroups.length >= limit);

  return (
    <div className={_cnSelection('flex w-80 flex-col gap-2.5 py-2.5', 'w-fit bg-popover text-popover-foreground')}>
      <div className={_cnSelection('flex border-border border-b pb-2.5', 'px-2')}>
        <div className='flex w-full flex-row justify-between'>
          <Button variant='ghost' size='sm' data-testid='vehicle-group-filter-clean' disabled={selectedGroups.length === 0} onClick={onClearFilter}>
            {t('groups.clear_selection')}
          </Button>
          <Button variant='ghost' size='sm' data-testid='vehicle-group-filter-sort' onClick={swapSortOrder}>
            <Icon size='sm' name='cb-sort-menu-asc' />
            {sortOrder === 'asc' ? t('groups.sort_az') : t('groups.sort_za')}
          </Button>
        </div>
      </div>
      <div className='px-2'>
        <InputGroup>
          <InputGroupAddon>
            <Icon name='search' size='sm' />
          </InputGroupAddon>
          <InputGroupInput
            data-testid='vehicle-group-filter-search-input'
            autoFocus
            aria-label={t('groups.search_placeholder')}
            placeholder={t('groups.search_placeholder')}
            value={searchBoxInput}
            onChange={(event) => setSearchBoxInput(event.target.value)}
          />
        </InputGroup>
      </div>
      <div className={_cnSelection('mt-2.5 flex max-h-56 flex-col', 'px-2')}>
        <SelectionList
          viewportHeight={300}
          rowHeight={28}
          items={groupsFiltered}
          loading={isLoading}
          empty={renderNoGroupsPlaceholder()}
          loadingContent={<SelectionListLoading />}
          renderItem={(group: VehicleGroup) => {
            const checkbox = (
              <div className='flex items-center gap-2'>
                <Checkbox
                  id={group.name}
                  data-testid={`vehicle-group-filter-${group.id}`}
                  data-cy={`vehicle-group-filter-${group.id}`}
                  disabled={disabled(group)}
                  checked={selectedGroups.some((selectedGroup) => selectedGroup.id === group.id)}
                  onCheckedChange={() => onChange(group)}
                  value={group.name}
                  title={group.name}
                />
                <Label htmlFor={group.name} className={cn('line-clamp-1 text-start', disabled(group) && 'text-muted-foreground')} title={group.name}>
                  {group.name}
                </Label>
              </div>
            );

            return (
              <div key={group.name} className='line-clamp-1 w-full pr-4'>
                <TooltipOnIntent render={checkbox} disabled={!validateGroupHasNoVehicle(group)}>
                  {t('groups.group_without_vehicles')}
                </TooltipOnIntent>
              </div>
            );
          }}
        />
      </div>
      <div className='flex flex-col gap-2.5 border-border border-t pt-2.5'>
        <div className={_cnSelection('flex items-center justify-between gap-2.5', 'flex flex-row justify-between px-2')}>
          <div className='flex flex-row'>
            <Typography
              variant='body'
              className='gap-1 break-words font-semibold text-foreground text-popover-foreground'
            >{`${selectedGroups.length}${limit ? `/${limit}` : ''}`}</Typography>
            &nbsp;
            <Typography variant='body' className='gap-1 break-words text-foreground text-popover-foreground'>
              {t('groups.selected')}
            </Typography>
          </div>
          <div className='ml-4 flex flex-row'>
            <Button variant='ghost' size='sm' data-testid='vehicle-group-filter-cancel' onClick={onCancel} className='px-4'>
              {t('groups.cancel')}
            </Button>
            <Button
              size='sm'
              data-testid='vehicle-group-filter-apply'
              onClick={onApplyFilter}
              disabled={vehicleGroups.length === 0 || !selectionChanged || selectedGroups.length > 30}
              className='ml-2 px-7'
            >
              {t('groups.apply')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiSelectVehicleGroupsSidebarOverlay;
